import React, { type ImgHTMLAttributes } from 'react';
import { graphql, useStaticQuery } from 'gatsby';

import type { Document } from '../model/Document';

export interface ImageAttributes extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet'> {
  size?: 'sm' | 'md' | 'lg';
  pictureClassName?: string;
}

export interface ImageProps extends ImageAttributes {
  src: Document | string;
}

export default function Image({ src, size, pictureClassName, ...props }: ImageProps) {
  if (src === null) {
    return <img alt={props.alt} {...props} />;
  }

  if (typeof src === 'string') {
    return <img alt={props.alt} src={src} {...props} />;
  }

  let sourceJpeg = src.cdn_ident;
  let sourceWebp: string = null;
  switch (size) {
    case 'sm':
      sourceJpeg = src.cdn_ident_s;
      sourceWebp = src.cdn_ident_webp_s;
      break;
    case 'md':
      sourceJpeg = src.cdn_ident_m;
      sourceWebp = src.cdn_ident_webp_m;
      break;
    case 'lg':
      sourceJpeg = src.cdn_ident_l;
      sourceWebp = src.cdn_ident_webp_l;
  }

  return (
    <picture className={pictureClassName}>
      {sourceWebp ? <source srcSet={sourceWebp} type="image/webp" /> : null}
      <img alt={props.alt} src={sourceJpeg ?? src.cdn_ident} {...props} />
    </picture>
  );
}

interface StaticImageProps extends Omit<ImgHTMLAttributes<HTMLImageElement>, 'src' | 'srcSet' | 'sizes'> {
  name: string;
}

interface QueryData {
  files: {
    nodes: {
      name: string;
      publicURL: string;
    }[];
  };
}

const sizeSuffixRegexp = new RegExp(/@(\dx)/);
const imageToSourceSet = it => {
  const sizeMatch = sizeSuffixRegexp.exec(it.name);
  if (!sizeMatch) return `${it.publicURL} 1x`;
  const [, size] = sizeMatch;
  return `${it.publicURL} ${size}`;
};
const mediaTypeMapping = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  svg: 'image/svg+xml',
  webp: 'image/webp',
};

export function StaticImage({ name, className, ...props }: StaticImageProps) {
  const data = useStaticQuery<QueryData>(graphql`
    query {
      files: allFile(filter: { extension: { in: ["jpg", "jpeg", "webp", "png", "svg"] } }) {
        nodes {
          name
          publicURL
        }
      }
    }
  `);

  const images = data.files.nodes.filter(node => node.name.match(new RegExp(`^${name}(@\\dx)?$`)));
  if (!images.length) return null;

  let baseImage = images.find(it => !sizeSuffixRegexp.test(it.name) && it.publicURL.match(/\.(png|jpe?g|svg)$/));
  if (!baseImage) {
    baseImage = images.find(it => !sizeSuffixRegexp.test(it.name));
  }
  if (!baseImage) return null;

  const [, baseMediaType] = baseImage.publicURL.match(/\.(png|jpe?g|webp|svg)$/);
  const sourceSets = images
    .filter(it => sizeSuffixRegexp.test(it.name) && it.publicURL.includes(`.${baseMediaType}`))
    .map(imageToSourceSet)
    .join(', ');
  const mediaTypes = [];
  for (const image of images) {
    if (sizeSuffixRegexp.test(image.name)) continue;
    const [, mediaType] = image.publicURL.match(/\.(png|jpe?g|webp|svg)$/);
    if (!mediaTypes.includes(mediaType) && mediaType !== baseMediaType) {
      mediaTypes.push(mediaType);
    }
  }
  const sources = mediaTypes.map(it => {
    const imagesWithType = images.filter(image => image.publicURL.includes(`.${it}`));
    return {
      mediaType: mediaTypeMapping[it],
      sourceSets: imagesWithType.map(imageToSourceSet).join(', '),
    };
  });

  const image = <img src={baseImage.publicURL} {...props} alt={props.alt ?? ''} srcSet={sourceSets} />;

  if (!sources.length) {
    return <div className={className}>{image}</div>;
  }

  return (
    <div className={className}>
      <picture>
        {sources.map(source => (
          <source key={source.mediaType} srcSet={source.sourceSets} type={source.mediaType} />
        ))}
        {image}
      </picture>
    </div>
  );
}
